/**
 * The catalogue: everything this tool can be asked about, and nothing else.
 *
 * A closed list is not a limitation here, it is the honesty mechanism. The
 * previous screen took free text, which meant it could always be asked for
 * something it had never measured; a list can only offer what a real run stands
 * behind. Two kinds of entry:
 *
 *  - a PROJECT, measured end to end on this machine (see `projects.ts`);
 *  - a RECURRING AUTOMATION, measured on the Hermes agents, which is the only
 *    corpus that carries a real frequency — up to 251 runs a month. That is what
 *    lets the answer be a monthly bill rather than a one-off.
 */
import { costOf, costWithoutCache } from './pricing'
import { t } from '../ui/lang'
import { BENCH_TASK_OF_TYPE, DERIVED_TYPES, DOCUMENTATION_SHARE, MODEL_TIERS, QUALITY_CAP_POINTS, VALUE_GAP_POINTS, type ModelTier } from './config'
import {
  CAP_BENCH, capabilityOf, capProvenOn, convertedFromBench, DOMAINS, EXPERT_PREFERENCE,
  expertRankOf, generalistOf, scorePoints, selectPicks,
  type CapBench, type Domain, type DomainSource, type PickKind, type ScoreBasis,
} from './capability'
import {
  consumptionOf, measuredSpanFr, scaleBundle, unmeasuredBiasFr, type Consumption,
} from './consumption'
import {
  estimateProject, getProjectLibrary,
  type ProjectEstimate, type ProjectInputs,
} from './projects'
import { fitPlan, reliabilityOf, type PlanFit, type Reliability } from './subscriptions'
import type { Archetype, Library, ModelPrice, TokenBundle } from './types'

/** How the reader pays: per token, or a fixed month. It changes the unit of the price, and
 *  therefore who wins — not the rule that picks. */
export type Billing = 'api' | 'subscription'

export type ItemKind = 'project' | 'automation'

export interface CatalogItem {
  id: string
  kind: ItemKind
  label_fr: string
  /** One sentence saying what this job IS. A list of keywords ("site + API + base")
   *  named the parts and left the reader to guess the whole. */
  description_fr: string
  /** One concrete case, so a reader recognises their own without knowing the jargon. */
  example_fr: string
  runsPerMonth: number | null
  /** Declared, not measured: how capable a model has to be to hold this up. */
  tier: ModelTier
  /** Whether the agent has a screen to check at all. False for a headless service:
   *  a backend that announces « avec vérification du rendu à l'écran » prices a loop
   *  it will never run (20/09/2026). The reader can still switch it back on. */
  visualByDefault: boolean
  /** Which published benchmark is evidence about THIS job. Picking the wrong one
   *  is how a table ends up recommending a great essayist to build an API. */
  domain: Domain
  /** Declared from our own benchmark: the note above which this job shows no measurable gain.
   *  Null when we never benchmarked this kind of work — then nothing is capped. */
  capPoints: number | null
}

/** How a project type maps onto a published benchmark. The dev tiles are all
 *  software engineering, so they are judged on a software engineering index. */
const PROJECT_DOMAIN: Domain = 'coding_agent'

/** The automations read a state, decide, and write prose. No published index
 *  measures that per model, so they fall back to the generalist one — which is
 *  the honest match for the work, not a substitute for a missing bench. */
const AUTOMATION_DOMAIN: Domain = 'general'

/** Product-facing wording for the measured automation profiles. The archetypes
 *  are named after what they did; these are named after what someone would ask
 *  for. */
function automations(): Record<string, Omit<CatalogItem,
  'id' | 'kind' | 'runsPerMonth' | 'domain' | 'capPoints'
  | 'visualByDefault'>> {
  return {
  auto_short_task: {
    label_fr: t('Traiter une file, élément par élément',
      'Work a queue, one item at a time'),
    description_fr: t('À chaque élément qui arrive, l’agent le lit, décide quoi en faire '
      + 'et l’écrit au bon endroit.',
    'As each item arrives, the agent reads it, decides what to do with it and writes it '
      + 'where it belongs.'),
    example_fr: t('trier les demandes d’assistance', 'sorting support requests'),
    tier: 'leger',
  },
  auto_report: {
    label_fr: t('Un rapport automatique, chaque jour', 'An automatic report, every day'),
    description_fr: t('L’agent va voir où en sont les choses et en écrit un compte rendu, '
      + 'seul, à heure fixe.',
    'The agent goes and looks at where things stand and writes it up, on its own, at a '
      + 'fixed hour.'),
    example_fr: t('le point du matin sur un projet', 'the morning update on a project'),
    tier: 'leger',
  },
  auto_synthesis: {
    label_fr: t('Une synthèse de plusieurs sources', 'A digest of several sources'),
    description_fr: t('L’agent rassemble ce qui vient de plusieurs endroits et en tire un '
      + 'texte court, lisible d’un coup d’œil.',
    'The agent gathers what comes from several places and turns it into a short text you '
      + 'can read at a glance.'),
    example_fr: t('un brief tiré des mails et du calendrier',
      'a brief drawn from email and the calendar'),
    tier: 'standard',
  },
  auto_watch: {
    label_fr: t('Une veille large', 'Watching a wide field'),
    description_fr: t('L’agent parcourt beaucoup de sources, écarte le bruit et ne remonte '
      + 'que ce qui mérite votre attention.',
    'The agent goes through many sources, sets the noise aside and brings back only what '
      + 'deserves your attention.'),
    example_fr: t('suivre ce que publient des concurrents',
      'following what competitors publish'),
      tier: 'standard',
    },
  }
}

function projectExamples(): Record<string, string> {
  return {
    produit_complet: t('un service de réservation, avec son site et son app',
      'a booking service, with its site and its app'),
    web_app: t('un outil interne de suivi de dossiers', 'an internal case-tracking tool'),
    site_vitrine: t('un portfolio', 'a portfolio'),
    extension: t('un greffon qui ajoute une fonction à une page',
      'an add-on that puts a feature on a page'),
  }
}

/** Les types qu'on ne mesure pas seuls, mais qu'on sait découper (config.DERIVED_TYPES). */
function derivedMeta(): Record<string, {
  label_fr: string
  description_fr: string
  example_fr: string
}> {
  return {
  app_mobile: {
    label_fr: t('Une application mobile', 'A mobile app'),
    description_fr: t('L’app qu’on installe sur iOS et Android, elle seule : si le service '
      + 'derrière reste à écrire, prenez aussi l’API.',
    'The app you install on iOS and Android, on its own: if the service behind it is still '
      + 'to be written, take the API as well.'),
    example_fr: t('une app de suivi d’activité', 'an activity-tracking app'),
  },
  api_backend: {
    label_fr: t('Une API ou un backend', 'An API or a backend'),
    description_fr: t('Le moteur d’un service : il range les données, applique les règles '
      + 'et répond aux programmes qui l’appellent, sans écran.',
    'The engine of a service: it stores the data, applies the rules and answers the '
      + 'programs that call it, with no screen.'),
    example_fr: t('l’API d’une application qui existe déjà',
      'the API of an application that already exists'),
  },
  ecommerce: {
    label_fr: t('Une boutique en ligne', 'An online shop'),
    description_fr: t('Un magasin où l’on parcourt des produits, remplit un panier et paie '
      + 'en ligne.',
    'A shop where you browse products, fill a basket and pay online.'),
    example_fr: t('une boutique de marque', 'a brand’s own shop'),
    },
  }
}

/** Les travaux qui n'ont pas d'écran : la boucle visuelle ne leur est pas proposée
 *  par défaut. Déclaré, comme les parts — et réversible d'un clic à l'étape 2. */
const HEADLESS = new Set(['api_backend'])

const PROJECT_TIERS: Record<string, ModelTier> = {
  produit_complet: 'standard',
  web_app: 'standard',
  site_vitrine: 'standard',
  extension: 'leger',
}

export function catalogItems(library: Library): CatalogItem[] {
  const items: CatalogItem[] = []
  const projects = getProjectLibrary(library)
  if (projects) {
    for (const [id, type] of Object.entries(projects.types)) {
      items.push({
        id, kind: 'project',
        label_fr: t(type.label_fr, type.label_en),
        description_fr: t(type.description_fr, type.description_en),
        example_fr: projectExamples()[id] ?? '',
        runsPerMonth: null,
        tier: PROJECT_TIERS[id] ?? 'standard',
        visualByDefault: !HEADLESS.has(id),
        domain: PROJECT_DOMAIN,
        capPoints: QUALITY_CAP_POINTS[id] ?? null,
      })
    }
  }
  if (projects) {
    for (const [id, { base }] of Object.entries(DERIVED_TYPES)) {
      const meta = derivedMeta()[id]
      const type = projects.types[base]
      if (!meta || !type) continue
      items.push({
        id, kind: 'project',
        label_fr: meta.label_fr,
        description_fr: meta.description_fr,
        example_fr: meta.example_fr,
        runsPerMonth: null,
        tier: PROJECT_TIERS[base] ?? 'standard',
        visualByDefault: !HEADLESS.has(id),
        domain: PROJECT_DOMAIN,
        capPoints: QUALITY_CAP_POINTS[id] ?? null,
      })
    }
  }
  for (const archetype of library.archetypes) {
    const meta = automations()[archetype.id]
    if (!meta) continue
    items.push({
      id: archetype.id, kind: 'automation', ...meta,
      // Une automatisation n'a pas d'étape « ampleur » : les modificateurs projet ne
      // la touchent pas, et la boucle visuelle non plus.
      visualByDefault: false,
      runsPerMonth: archetype.runs_per_month,
      domain: AUTOMATION_DOMAIN,
      capPoints: QUALITY_CAP_POINTS[archetype.id] ?? null,
    })
  }
  return items
}

function bundleOf(archetype: Archetype): TokenBundle {
  const median = archetype.tokens_median
  return {
    in: median.in, out: median.out, cw: median.cw, cr: median.cr,
    cw_5m: median.cw, cw_1h: 0,
    turns: archetype.turns.median,
  }
}

export interface ModelQuote {
  model: ModelPrice
  /** The job at API rates, on the volume THIS model spends (see `consumption.ts`). */
  costUsd: number
  /** What the reader pays under the chosen billing: `costUsd`, or a month of the plan below.
   *  null when the model cannot be bought that way — it is then listed, never picked. */
  billedUsd: number | null
  /** Tokens this model spends on the job, cache re-reads included. */
  tokens: number
  consumption: Consumption
  /** Subscription billing only: the plan quoted and what the job does to its ceiling. */
  plan: PlanFit | null
  /** Our own completion record, or null for a model we never ran. */
  reliability: Reliability | null
  tier: ModelTier
  /** 0..1 on the index that matches this task, or null when that index has no
   *  result for this model. Never invented. */
  score: number | null
  basis: ScoreBasis
  /** The generalist index, shown only for models the task index does not cover.
   *  Context for the reader, never an input to the ranking. */
  generalist: number | null
  /** Which of the three cards this model holds — possibly two of them. */
  picks: PickKind[]
  /** On the Pareto frontier: nothing in the grid is both cheaper and better. */
  onFrontier: boolean
  /** Score per dollar of this job. The ordering, not a number to show: a ratio of
   *  a bounded score to an unbounded price compresses everything below the leader
   *  into noise. */
  value: number | null
  /** Rank among models scored on this task. 0 = unscored, listed at the bottom. */
  rank: number
}

export interface Pick {
  kind: PickKind
  label_fr: string
  model: ModelPrice
  /** What the reader pays under the chosen billing. */
  costUsd: number
  /** 0..1 on the task's index. */
  score: number
  reason_fr: string
  quote: ModelQuote
}

/** What the quality cap did here, for the page to say out loud. */
export interface CapView {
  /** The printed note above which our own benchmark stops measuring a gain on this job. */
  points: number
  binds: boolean
  provenUsed: boolean
  /** The note the middle card has to reach. */
  floor: number
  /** Better-noted, dearer models the cap sets aside. */
  aside: ModelPrice[]
  /** What the top card would have named without the cap. */
  instead: { model: ModelPrice; costUsd: number; ratio: number } | null
  /** The benchmark that set the cap, so the page can cite it. */
  bench: CapBench | null
}

export interface Recommendations {
  billing: Billing
  picks: Pick[]
  /** Null when this kind of work has no cap: everything then reads as it always did. */
  cap: CapView | null
  /** Subscription billing: models scored on this job that sell no plan — ils ne peuvent pas
   *  être chiffrés au mois. Gardé pour les tests ; la page ne les nomme plus. */
  noPlan: ModelPrice[]
  /** Un modèle sans note sur cette tâche, mais nettement moins cher que la
   *  recommandation la moins chère. Le taire laisserait croire qu'il n'existe
   *  rien de moins cher. */
  cheaperUnscored: { model: ModelPrice; costUsd: number; ratio: number } | null
  domain: DomainSource
  /** Models in the grid with a published result on this index. */
  pool: number
  /** Models the index does not cover, noted on this domain from our own runs, converted. */
  ownScored: ModelPrice[]
  /** Models with no note at all on this domain — excluded from the picks, not judged. */
  unscored: number
  /** 'bench' as soon as one of our own measurements was used. */
  basis: ScoreBasis
  /** The limit that can flip a conclusion. Deliberately does NOT restate the
   *  source: the interface prints that once, next to the link. */
  caveat_fr: string
}

export interface ItemEstimate extends Omit<ProjectEstimate, 'type'> {
  item: CatalogItem
  /** Automations only: one run, and a month of runs. */
  perRunUsd: number | null
  perMonthUsd: number | null
  quotes: ModelQuote[]
  recommendations: Recommendations | null
  typeLabel: string
  typeNote: string
  predictable: boolean
}

export interface CatalogInputs extends ProjectInputs {
  /** Kept for later. The tool quotes a UNIT price and leaves the multiplication
   *  to whoever knows their own cadence. */
  runsPerMonth: number | null
  /** Per use when absent: the workshop has no subscription view. */
  billing?: Billing
}

/** The card titles. Fixed: the goal of each card lives in selectPicks, not in its name. */
function pickLabels(): Record<PickKind, string> {
  return {
    cheapest: t('Le moins cher', 'The cheapest'),
    value: t('Qualité / Prix', 'Value for money'),
    best: t('Le meilleur', 'The best'),
  }
}

/** A multiplier inside French prose. The table has its own formatter; this one
 *  exists so the engine stays DOM-free and still writes a comma. */
function ratio(value: number): string {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: value >= 10 ? 0 : 1 })
}

/** The token split a subscription ceiling is priced on. Cache writes are fresh input. */
function splitOf(bundle: TokenBundle) {
  return { input_fresh: bundle.in + bundle.cw, cache_read: bundle.cr, output: bundle.out }
}

/**
 * Price the same work on every model in the grid, and score each one on the
 * benchmark that matches the work.
 *
 * The comparison is the single most valuable thing the tool shows — the spread is
 * two orders of magnitude. The corpus is entirely Anthropic, so each model's volume
 * is first scaled by what it was measured to spend on our nine tasks; a model we
 * never ran keeps the Claude volume and says so.
 *
 * Under subscription billing the price becomes a month of the cheapest plan that
 * holds the job, and the tokens stop being the price and become the tie-break.
 *
 * The ranking only ever covers models the task's index has actually scored. The
 * rest are priced, listed, and left unranked.
 */
export function quoteModels(
  bundle: TokenBundle,
  library: Library,
  cacheActive: boolean,
  domain: Domain,
  billing: Billing = 'api',
  capPoints: number | null = null,
  /** Part du jugement qui bascule sur l'écriture, quand le travail doit être documenté. */
  writingShare = 0,
  /** La tâche de notre bench qui ressemble à ce travail, quand il y en a une. */
  task: string | null = null,
): { quotes: ModelQuote[]; recommendations: Recommendations | null } {
  const quotes: ModelQuote[] = library.pricing.models.map((model) => {
    const capability = capabilityOf(domain, model.id, writingShare, task)
    const consumption = consumptionOf(model.id)
    const own = scaleBundle(bundle, consumption.factor)
    const tokens = own.in + own.out + own.cw + own.cr
    const costUsd = cacheActive ? costOf(own, model) : costWithoutCache(own, model)
    const plan = billing === 'subscription' ? fitPlan(model.id, splitOf(own), tokens) : null
    const billedUsd = billing === 'api' ? costUsd : plan?.plan.usd_per_month ?? null
    return {
      model,
      costUsd,
      billedUsd,
      tokens,
      consumption,
      plan,
      reliability: reliabilityOf(model.id),
      tier: MODEL_TIERS[model.id] ?? 'standard',
      score: capability.value,
      basis: capability.basis,
      generalist: capability.value === null ? generalistOf(model.id) : null,
      picks: [],
      onFrontier: false,
      // Un plafond trop petit pour un mois n'écarte plus le modèle : il n'empêche pas de
      // faire le travail, il l'étale sur plusieurs mois — et seuls les éditeurs qui
      // PUBLIENT un plafond se faisaient écarter, ce qui punissait leur franchise
      // (Armand, 21/09/2026). La page dit sous les cartes ce que les quotas changent.
      value: capability.value !== null && billedUsd !== null && billedUsd > 0
        ? capability.value / billedUsd : null,
      rank: 0,
    }
  })

  type Priced = ModelQuote & { score: number; billedUsd: number }
  const scored = quotes.filter((quote): quote is Priced =>
    quote.score !== null && quote.billedUsd !== null && quote.billedUsd > 0)
  const monthly = billing === 'subscription'

  const points = scored.map((quote) => ({
    id: quote.model.id, costUsd: quote.billedUsd, score: quote.score,
    // Per month, the quota share first: Z.ai counts credits, not tokens, and the two rank
    // GLM-5.3 and its Flash sibling in opposite orders. An unknown share sorts last.
    tiebreak: monthly
      ? [quote.plan?.share ?? Number.POSITIVE_INFINITY, quote.tokens] : [quote.tokens],
    tested: quote.reliability !== null,
    expertRank: expertRankOf(domain, quote.model.id),
    capProven: capProvenOn(domain, quote.model.id),
  }))
  const picks = selectPicks(points, VALUE_GAP_POINTS / 100, capPoints)
  const onFrontier = new Set(picks.frontier)
  for (const quote of quotes) onFrontier.has(quote.model.id) && (quote.onFrontier = true)

  // Scored models first, ordered on what a euro buys; unscored below, by price.
  // Sorting the whole table by price put capable mid-priced models behind cheap
  // weak ones; sorting by score put the most expensive first. Neither answers the
  // question being asked. Under a subscription many share a price: fewer tokens first.
  quotes.sort((a, b) => {
    if (a.value !== null && b.value !== null) return b.value - a.value || a.tokens - b.tokens
    if (a.value !== null) return -1
    if (b.value !== null) return 1
    if (a.billedUsd === null || b.billedUsd === null) {
      return a.billedUsd === null ? (b.billedUsd === null ? a.costUsd - b.costUsd : 1) : -1
    }
    return a.billedUsd - b.billedUsd
  })
  let rank = 0
  for (const quote of quotes) quote.rank = quote.value !== null ? ++rank : 0

  const source = DOMAINS[domain]
  const find = (id: string | null) => id === null
    ? undefined : scored.find((quote) => quote.model.id === id)
  const cheapest = find(picks.cheapest)
  const best = find(picks.best)
  const value = find(picks.value)
  if (!cheapest || !best || !value) {
    return { quotes, recommendations: null }
  }

  const bestPoints = scorePoints(best.score)
  const pointsBelow = (quote: Priced) => bestPoints - scorePoints(quote.score)
  const plural = (n: number) => `${n} point${n > 1 ? 's' : ''}`
  const perMonth = monthly ? ' par mois' : ''
  // A note converted from our own runs carries an uncertainty an index note does not show.
  const converted = (quote: Priced) => convertedFromBench(domain, quote.model.id)
  const chosen: Pick[] = []
  const add = (kind: PickKind, quote: Priced, reason_fr: string) => {
    quote.picks.push(kind)
    chosen.push({
      kind, label_fr: pickLabels()[kind], model: quote.model,
      costUsd: quote.billedUsd, score: quote.score, reason_fr, quote,
    })
  }

  // Le moins cher. The quota claim is made only when every model at this price has a known
  // share: comparing a known share with an unknown one says nothing about which drains slower.
  const rivals = scored.filter((quote) => quote.billedUsd === cheapest.billedUsd
    && quote.model.id !== cheapest.model.id)
  const soberest = rivals.length > 0 && cheapest.plan?.share != null
    && rivals.every((quote) => quote.plan?.share != null
      && quote.plan.share >= cheapest.plan!.share!)
  const cheapGap = pointsBelow(cheapest)
  // With a cap, the yardstick is the cap itself: the note of the top card is no longer what
  // the reader is being compared against.
  const capBinds = picks.cap?.binds === true
  const capGap = capBinds ? picks.cap!.points - scorePoints(cheapest.score) : 0
  add('cheapest', cheapest,
    (monthly
      ? `L’abonnement le moins cher qui donne accès à un modèle noté sur ce travail`
        + (rivals.length === 0 ? '. '
          : soberest ? ` — et, à ce prix, celui qui consomme le moins de son quota. `
            : ` ; d’autres modèles sont au même prix, et aucun plafond publié ne dit lequel `
              + `ménage le mieux son quota. `)
      : `Le moins cher des modèles notés sur ce type de travail. `)
    + (capBinds
      ? (capGap <= 0
        ? `Sa note atteint déjà le plafond de qualité de ce travail (${picks.cap!.points}/100).`
        : `Sa note est à ${plural(capGap)} du plafond de qualité de ce travail `
          + `(${picks.cap!.points}/100).`)
      : cheapGap <= 0
        ? `Sa note égale celle de la carte « Le meilleur ».`
        : converted(cheapest)
          ? `Sa note est à environ ${plural(cheapGap)} de la carte « Le meilleur », selon la `
            + `conversion de nos propres tests ; l’écart réel peut atteindre une dizaine de `
            + `points.`
          : `Sa note est à ${plural(cheapGap)} de celle de la carte « Le meilleur ».`))

  // Qualité / Prix.
  const valueGap = pointsBelow(value)
  add('value', value, capBinds
    ? (value === cheapest && value === best
      ? `Déjà le moins cher, et déjà au plafond de qualité : aucun modèle moins cher `
        + `n’atteint ce niveau, et aucun plus cher ne fait mieux sur ce travail.`
      : value === cheapest
        ? `Déjà le moins cher des modèles à ${VALUE_GAP_POINTS} points ou moins du plafond de `
          + `qualité (${picks.cap!.points}/100) : aucun modèle assez proche ne coûte moins.`
        : `Le moins cher des modèles à ${VALUE_GAP_POINTS} points ou moins du plafond de `
          + `qualité (${picks.cap!.points}/100)`
          + (value === best ? `, et c’est lui qui tient déjà la carte « Le meilleur ».`
            : ` : ${ratio(best.billedUsd / value.billedUsd)}× moins cher${perMonth} que la `
              + `carte « Le meilleur ».`))
    : (value === cheapest
      ? `Déjà le moins cher, et ${valueGap <= 0 ? 'à égalité avec la meilleure note'
        : `à ${plural(valueGap)} seulement de la meilleure note`} : aucun modèle assez proche `
        + `du meilleur ne coûte moins.`
      : value === best
        ? `Aucun modèle à ${VALUE_GAP_POINTS} points ou moins de la meilleure note ne coûte moins `
          + `cher que le meilleur lui-même : ici, la meilleure qualité au plus bas coût, c’est lui.`
        : `Le moins cher des modèles à ${VALUE_GAP_POINTS} points ou moins de la meilleure note : `
          + `${plural(valueGap)} en dessous, pour ${ratio(best.billedUsd / value.billedUsd)}× `
          + `moins cher${perMonth} que la carte « Le meilleur ».`)
      + (converted(value) && value !== cheapest
        ? ` Sa note vient de nos propres tests, convertie : à quelques points près.` : ''))

  // Le meilleur.
  const spread = cheapest.billedUsd > 0 ? best.billedUsd / cheapest.billedUsd : 1
  const price = best.billedUsd === cheapest.billedUsd ? 'au même prix que le moins cher'
    : spread < 1.1 ? 'à peine plus cher que le moins cher'
      : `à ${ratio(spread)}× le prix du moins cher`
  // Why the top card went to this model rather than another printing the same note.
  const tied = scored.filter((quote) => quote !== best && scorePoints(quote.score) === bestPoints)
  const expertOrder = EXPERT_PREFERENCE[domain]?.order ?? []
  const byExpert = tied.filter((quote) => expertOrder.includes(quote.model.id)
    && expertOrder.includes(best.model.id))
  const byTest = tied.filter((quote) => !byExpert.includes(quote)
    && best.reliability !== null && quote.reliability === null)
  const names = (list: Priced[]) => list.map((quote) => quote.model.label).join(', ')
  const instead = picks.cap?.instead
    ? scored.find((quote) => quote.model.id === picks.cap!.instead) ?? null
    : null
  add('best', best, capBinds
    // No expert verdict and no « we tested it » here: both exist to separate models on
    // quality, and the cap has just said this field cannot be separated on quality.
    ? `Le premier modèle qui atteint le plafond de qualité de ce travail `
      + `(${picks.cap!.points}/100), et le moins cher à l’atteindre. Au-dessus, notre bench ne `
      + `mesure plus de gain. `
      + (picks.cap!.aside.length > 0
        ? `Le plafond écarte ${picks.cap!.aside.length} modèle`
          + `${picks.cap!.aside.length > 1 ? 's' : ''} mieux noté`
          + `${picks.cap!.aside.length > 1 ? 's' : ''} et plus cher`
          + `${picks.cap!.aside.length > 1 ? 's' : ''}. ` : '')
      + (picks.cap!.provenUsed
        ? `Cette carte ne va qu’à un modèle que notre bench a vu faire ce travail.`
        : `Aucun modèle de ce niveau n’est passé par notre bench : la carte revient au moins `
          + `cher, et nous le disons plutôt que de le taire.`)
    : `La meilleure note sur ce type de travail, ${price}. `
    + (byExpert.length > 0
      ? `À note égale avec ${names(byExpert)}, on retient celui que notre expert juge meilleur `
        + `à l’usage sur ce type de travail — un écart qu’il dit faible. `
      : '')
    + (byTest.length > 0
      ? `À note égale avec ${names(byTest)}, on retient le modèle que nous avons testé. `
      : '')
    + (monthly
      ? `À prix fixe, ce qu’il consomme ne coûte rien de plus tant que le quota tient.`
      : `Le choix quand la qualité compte plus que le prix.`))

  // Le vrai piège de l'exclusion : un modèle écarté faute de note peut être
  // radicalement moins cher que celui qu'on met en avant. La grille a déjà
  // recommandé un modèle 24 fois plus cher en relecture qu'un voisin non noté.
  const unscored = quotes
    .filter((quote): quote is ModelQuote & { billedUsd: number } =>
      quote.score === null && quote.billedUsd !== null && quote.billedUsd > 0)
    .sort((a, b) => a.billedUsd - b.billedUsd)[0]
  const cheaperUnscored = unscored && unscored.billedUsd < cheapest.billedUsd / 1.5
    ? {
      model: unscored.model,
      costUsd: unscored.billedUsd,
      ratio: cheapest.billedUsd / unscored.billedUsd,
    }
    : null

  const factors = measuredSpanFr() ?? 'une part inconnue de'
  return {
    quotes,
    recommendations: {
      billing,
      picks: chosen,
      cap: picks.cap === null ? null : {
        points: picks.cap.points,
        binds: picks.cap.binds,
        provenUsed: picks.cap.provenUsed,
        floor: picks.cap.floor,
        aside: picks.cap.aside
          .map((id) => scored.find((quote) => quote.model.id === id)?.model)
          .filter((model): model is ModelPrice => model !== undefined),
        instead: instead
          ? { model: instead.model, costUsd: instead.billedUsd,
            ratio: instead.billedUsd / best.billedUsd }
          : null,
        bench: CAP_BENCH[domain] ?? null,
      },
      noPlan: monthly
        ? quotes.filter((quote) => quote.score !== null && quote.billedUsd === null)
          .map((quote) => quote.model)
        : [],
      cheaperUnscored,
      domain: source,
      // Counted on the note, not on the cards: a model scored here but sold per use only, or
      // too big for its plans, is explained by its own note, not lumped with the unscored.
      pool: quotes.filter((quote) => quote.score !== null
        && !convertedFromBench(domain, quote.model.id)).length,
      ownScored: quotes.filter((quote) => quote.score !== null
        && convertedFromBench(domain, quote.model.id)).map((quote) => quote.model),
      unscored: quotes.filter((quote) => quote.score === null).length,
      basis: scored.some((quote) => quote.basis === 'bench') ? 'bench' : 'published',
      caveat_fr: (monthly
        ? `Le prix est un mois entier du forfait le moins cher qui donne accès au modèle `
          + `et dont le plafond, quand on le connaît, tient le projet dans le mois. Les `
          + `plafonds ne sont connus que chez deux éditeurs ; ailleurs, la carte le dit. `
        : '')
        + `Nos projets de référence ont été construits avec Claude. Sur nos neuf tâches de `
        + `test, les autres modèles ont consommé ${factors} ce volume : chaque modèle mesuré `
        + `est chiffré à sa propre consommation. Un modèle non-Claude jamais mesuré reste au `
        + `volume de Claude, ${unmeasuredBiasFr()}.`,
    },
  }
}

export function estimateItem(
  inputs: CatalogInputs,
  library: Library,
): ItemEstimate | null {
  const item = catalogItems(library).find((entry) => entry.id === inputs.typeId)
  if (!item) return null
  const model = library.pricing.models.find((m) => m.id === inputs.modelId)
  if (!model) return null

  if (item.kind === 'project') {
    const base = estimateProject(inputs, library)
    if (!base) return null
    const { quotes, recommendations } = quoteModels(
      base.tokens, library, inputs.cacheActive, item.domain, inputs.billing, item.capPoints,
      inputs.modifiers['documented'] === true ? DOCUMENTATION_SHARE : 0,
      BENCH_TASK_OF_TYPE[item.id] ?? null)
    const { type, ...rest } = base
    return {
      ...rest, item, quotes, recommendations,
      perRunUsd: null, perMonthUsd: null,
      // Le nom du travail CHOISI : un type dérivé est chiffré sur un autre, mais le
      // lecteur qui a cliqué « une application mobile » doit se lire, lui.
      typeLabel: item.label_fr,
      typeNote: type.note_fr,
      predictable: type.predictable,
    }
  }

  const archetype = library.archetypes.find((a) => a.id === item.id)
  if (!archetype) return null
  const bundle = bundleOf(archetype)
  // The chosen model's own volume, as for projects; the comparison scales every model itself.
  const factorOf = consumptionOf(model.id).factor
  // A UNIT price, deliberately. How often you would run it is your arithmetic,
  // not ours: multiplying by an observed frequency hands back a monthly bill
  // built on somebody else's cadence.
  const own = scaleBundle(bundle, factorOf)
  const perRun = inputs.cacheActive
    ? costOf(own, model) : costWithoutCache(own, model)
  const { quotes, recommendations } = quoteModels(
    bundle, library, inputs.cacheActive, item.domain, inputs.billing, item.capPoints)

  const sampleCosts = archetype.token_samples
    .map((sample) => scaleBundle(sample, factorOf))
    .map((sample) => inputs.cacheActive
      ? costOf(sample, model) : costWithoutCache(sample, model))
    .sort((a, b) => a - b)
  const low = sampleCosts[0] ?? perRun
  const high = sampleCosts[sampleCosts.length - 1] ?? perRun

  const warnings: string[] = [
    'Le prix est celui d’une seule exécution : à vous de dire combien de fois par mois '
    + 'vous la lanceriez.',
  ]
  if (archetype.shape_provenance !== 'observed') {
    warnings.push(
      'La médiane est mesurée ; la dispersion autour d’elle est estimée.')
  }
  warnings.push(
    'Une tâche du même genre sur vos données peut demander bien plus, ou bien moins, '
    + 'de matière à lire.')

  const humanCostUsd = inputs.humanDays && inputs.humanDays > 0
    ? inputs.humanDays * inputs.dailyRateUsd : null

  return {
    item, model, quotes, recommendations,
    tokens: bundle,
    costUsd: perRun,
    rangeUsd: [low, high],
    noCacheUsd: costWithoutCache(own, model),
    reference: {
      name: archetype.name_fr, type: 'automation', signals: [],
      episodes: 1, api_calls: Math.round(archetype.turns.median),
      cost_usd: perRun, worked_hours: 0, span_days: null,
      families: {}, visual_share_of_requests: 0, visual_cost_share: 0,
      tokens: { in: bundle.in, out: bundle.out, cw: bundle.cw, cr: bundle.cr },
    },
    workedHours: 0,
    requests: Math.round(archetype.turns.median),
    appliedModifiers: [],
    perRunUsd: perRun,
    perMonthUsd: null,
    humanCostUsd,
    savingUsd: humanCostUsd === null ? null : humanCostUsd - perRun,
    ratioHumanOverAgent: humanCostUsd === null || perRun <= 0
      ? null : humanCostUsd / perRun,
    breakEvenDays: inputs.dailyRateUsd > 0 ? perRun / inputs.dailyRateUsd : null,
    warnings,
    typeLabel: item.label_fr,
    typeNote: archetype.description_fr,
    predictable: archetype.n_samples >= 20,
  }
}
